import { inject, Injectable } from '@angular/core';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';
import { BASE_URL_TOKEN, QuoteItemLine } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface NationalSupplierSessionResult {
    sessionId: string;
    status: string;
    sectionIndex: number;
    itemIndex: number;
    /** Stable per-item key captured at session creation, used to target the merge instead of the positional
     * sectionIndex/itemIndex, which go stale if the item list is reordered/edited while the session is open. Absent
     * on sessions created before this field existed. */
    targetItemKey?: string | null;
    lines: QuoteItemLine[];
}

/** Mirrors the backend NationalSupplierSearchType enum (serialized as its string name). */
export type NationalSupplierSearchType = 'Part' | 'Tire';

/** A tire fitment option for a vehicle, used to populate the tire-size picker. */
export interface NationalSupplierTireSize {
    tireId: string;
    name: string;
}

/** The maintenance-schedule mileage/time checkpoints for a vehicle. */
export interface NationalSupplierServiceIntervals {
    intervals: number[];
    intervalType: string;
}

/** A single service due at a maintenance interval, shaped for the picker and for building a labor line. */
export interface NationalSupplierScheduledService {
    applicationId: number;
    name: string;
    action: string | null;
    hours: number;
    skill: string | null;
    /** The branch's currently-effective Raptor labor rate matched by name, or null when none is configured. */
    rate: number | null;
}

/** Maintenance-schedule severity variant. */
export type NationalSupplierScheduleSeverity = 'Normal' | 'Severe';

@Injectable({
    providedIn: 'root',
})
export class NationalSupplierService {
    private readonly http = inject(HttpClient);
    private readonly base_url = inject(BASE_URL_TOKEN);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    createSession(
        tenantId: string,
        branchId: string,
        quoteId: string | null,
        poNumber: string | null,
        vin: string,
        sectionIndex: number,
        itemIndex: number,
        itemName: string | null,
        searchType: NationalSupplierSearchType = 'Part',
        tireId: string | null = null,
        width: number | null = null,
        height: number | null = null,
        rim: number | null = null,
        targetItemKey: string | null = null,
    ): Observable<{ id: string; redirectUrl: string }> {
        const url = `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/create-session`;
        const body = {
            quoteId,
            poNumber,
            vin,
            itemName,
            searchType,
            tireId,
            width,
            height,
            rim,
            sectionIndex,
            itemIndex,
            targetItemKey,
            // The redirect URL is used by National Supplier to close the window after processing.
            redirectUrl: "javascript:window.close('','_parent','');",
        };
        return this.http.post<{ id: string; redirectUrl: string }>(url, body, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Retires a session once its cart has been merged into the quote form, so the re-open/focus backstop no longer
     * resurfaces it. Best-effort; a failure just means the session may reappear until the server-side cleanup sweep.
     */
    acknowledgeSession(tenantId: string, branchId: string, sessionId: string): Observable<void> {
        const url = `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/session/${sessionId}/acknowledge`;
        return this.http.post<void>(url, null, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Returns the supplier's tire fitments for a vehicle VIN, used to populate the tire-size picker. Resolves to an
     * empty list when the lookup is unavailable, so the caller falls back to manual tire-size entry.
     */
    getTireSizes(tenantId: string, branchId: string, vin: string): Observable<NationalSupplierTireSize[]> {
        const url = `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/tire-sizes?vin=${encodeURIComponent(vin)}`;
        return this.http.get<NationalSupplierTireSize[]>(url, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Returns the completed, not-yet-committed supplier sessions for a quote. Used to merge parts that arrived
     * while the live SignalR nudge could not be delivered (re-open/focus backstop).
     */
    getCompletedQuoteSessions(tenantId: string, branchId: string, quoteId: string): Observable<NationalSupplierSessionResult[]> {
        const url = `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/quote/${quoteId}/sessions`;
        return this.http.get<NationalSupplierSessionResult[]>(url, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Returns the supplier's maintenance-schedule checkpoints for a vehicle VIN, used to populate the interval
     * picker. Resolves to an empty result when the lookup is unavailable. Synchronous read -- no punchout session.
     */
    getServiceIntervals(
        tenantId: string,
        branchId: string,
        vin: string,
        severity: NationalSupplierScheduleSeverity = 'Normal',
    ): Observable<NationalSupplierServiceIntervals> {
        const url =
            `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/service-intervals` +
            `?vin=${encodeURIComponent(vin)}&severity=${severity}`;
        return this.http.get<NationalSupplierServiceIntervals>(url, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Returns the services due at a chosen maintenance interval for a vehicle VIN. Resolves to an empty list when
     * the lookup is unavailable.
     */
    getScheduledServices(
        tenantId: string,
        branchId: string,
        vin: string,
        intervalValue: number,
        severity: NationalSupplierScheduleSeverity = 'Normal',
    ): Observable<NationalSupplierScheduledService[]> {
        const url =
            `${this.base_url}v1/nationalsupplier/tenant/${tenantId}/branch/${branchId}/scheduled-services` +
            `?vin=${encodeURIComponent(vin)}&intervalValue=${intervalValue}&severity=${severity}`;
        return this.http.get<NationalSupplierScheduledService[]>(url, {
            headers: this.httpHeadersWithNoLoading,
        });
    }
}
